/** @jsxImportSource @emotion/react */
import { useState, useRef } from "react";
import {
  accessAPI,
  Boton,
  Titulo,
  Formulario,
  Sidebar,
  useFuncionalidad,
  TablaFiltrable
} from "../../../../shared";
import SimuladorAutocompletar, {
  CamposDisponibles
} from "./SimuladorAutocompletar";

import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { Toast } from "primereact/toast";
import { Menu } from "primereact/menu";
import { confirmDialog } from "primereact/confirmdialog";

export default function ProductoCamposPropios({
  productoCamposPropios,
  certificadoTipoId
}) {
  const conUrsea = useFuncionalidad("URSEA");
  const [campos, setCampos] = useState(productoCamposPropios);
  const [sidebarNuevoVisible, setSidebarNuevoVisible] = useState(false);
  const [sidebarEditarVisible, setSidebarEditarVisible] = useState(false);
  // El autocompletar va en su propio sidebar: la lista de campos disponibles
  // tiene que quedar pegada al editor, y en el formulario general terminaba
  // debajo de todos los demás campos.
  const [sidebarAutocompletarVisible, setSidebarAutocompletarVisible] = useState(false);
  // El texto que hay en el editor, para poder probarlo antes de guardarlo.
  const [borradorScript, setBorradorScript] = useState("");
  const [campoEditando, setCampoEditando] = useState(null);
  const [loaderEnvio, setLoaderEnvio] = useState(false);
  const toast = useRef(null);
  const menuRef = useRef(null);
  const filaSeleccionada = useRef(null);

  function agregarCampo(datosFormulario) {
    setLoaderEnvio(true);
    accessAPI(
      "POST",
      `admin/producto/certificadotipo/${certificadoTipoId}/productocampospropios`,
      datosFormulario,
      (respuesta) => {
        setCampos((prev) => [...prev, respuesta]);
        setSidebarNuevoVisible(false);
        setLoaderEnvio(false);
        toast.current?.show({
          severity: "success",
          summary: "Agregado",
          detail: "Campo agregado exitosamente"
        });
      },
      (respuesta) => {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al agregar el campo",
          sticky: true
        });
        setLoaderEnvio(false);
      }
    );
  }

  function editarCampo(datosFormulario) {
    setLoaderEnvio(true);
    accessAPI(
      "PUT",
      `admin/producto/config/productocampospropios/${campoEditando.id}`,
      datosFormulario,
      (respuesta) => {
        setCampos((prev) =>
          prev.map((c) =>
            c.id === campoEditando.id
              ? { ...c, ...respuesta }
              : c
          )
        );
        setSidebarEditarVisible(false);
        setLoaderEnvio(false);
        setCampoEditando(null);
        toast.current?.show({
          severity: "success",
          summary: "Actualizado",
          detail: "Campo actualizado exitosamente"
        });
      },
      (respuesta) => {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al actualizar el campo",
          sticky: true
        });
        setLoaderEnvio(false);
      }
    );
  }

  function eliminarCampo(campo) {
    accessAPI(
      "DELETE",
      `admin/producto/productocampospropios/${campo.id}`,
      null,
      () => {
        setCampos((prev) => prev.filter((c) => c.id !== campo.id));
        toast.current?.show({
          severity: "success",
          summary: "Eliminado",
          detail: "Campo eliminado exitosamente"
        });
      },
      (respuesta) => {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al eliminar el campo",
          sticky: true
        });
      }
    );
  }

  function onRowReorder(e) {
    setCampos(e.value);
    const ids = e.value.map((c) => c.id);
    accessAPI(
      "PUT",
      "admin/producto/productocampospropios/reordenar",
      { ids },
      () => {},
      (respuesta) => {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al reordenar",
          sticky: true
        });
      }
    );
  }

  function abrirEditar(campo) {
    setCampoEditando(campo);
    setSidebarEditarVisible(true);
  }

  function abrirAutocompletar(campo) {
    setCampoEditando(campo);
    setBorradorScript(campo.autocompletarScript ?? "");
    setSidebarAutocompletarVisible(true);
  }

  function guardarAutocompletar(datosFormulario) {
    setLoaderEnvio(true);
    accessAPI(
      "PUT",
      `admin/producto/config/productocampospropios/${campoEditando.id}`,
      { autocompletarscript: datosFormulario.autocompletarscript || null },
      (respuesta) => {
        setCampos((prev) =>
          prev.map((c) => (c.id === campoEditando.id ? { ...c, ...respuesta } : c))
        );
        setSidebarAutocompletarVisible(false);
        setCampoEditando(null);
        setLoaderEnvio(false);
        toast.current?.show({
          severity: "success",
          summary: "Actualizado",
          detail: "Autocompletar actualizado"
        });
      },
      (respuesta) => {
        setLoaderEnvio(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta?.[0]?.msg || "No se pudo guardar el script",
          sticky: true
        });
      }
    );
  }

  const menuItems = [
    {
      label: "Editar",
      icon: "pi pi-pencil",
      command: () => abrirEditar(filaSeleccionada.current)
    },
    {
      label: "Autocompletar",
      icon: "pi pi-bolt",
      command: () => abrirAutocompletar(filaSeleccionada.current)
    },
    {
      label: "Eliminar",
      icon: "pi pi-trash",
      className: "text-red-500",
      command: () => {
        confirmDialog({
          message: "¿Eliminar este campo?",
          icon: "pi pi-exclamation-circle",
          accept: () => eliminarCampo(filaSeleccionada.current)
        });
      }
    }
  ];

  const agregarFormConfig = {
    secciones: [
      {
        campos: [
          {
            nombre: "etiqueta",
            tipoitemformularioid: 1,
            etiqueta: "Etiqueta",
            requerido: true
          },
          {
            nombre: "notas",
            tipoitemformularioid: 1,
            etiqueta: "Notas en formulario"
          },
          { nombre: "unidades", tipoitemformularioid: 1, etiqueta: "Unidades" },
          {
            nombre: "tipo",
            tipoitemformularioid: 5,
            etiqueta: "Tipo de datos",
            requerido: true,
            sinOpcionInicial: true,
            opciones: [
              { value: "string", etiqueta: "Texto" },
              { value: "int", etiqueta: "Número entero" },
              { value: "float", etiqueta: "Número decimal" }
            ]
          },
          {
            nombre: "opciones",
            tipoitemformularioid: 13,
            etiqueta: "Opciones"
          },
          {
            nombre: "autocompleteScript",
            tipoitemformularioid: 11,
            etiqueta: "Script de autocomplete"
          },
          {
            nombre: "nombre",
            tipoitemformularioid: 1,
            etiqueta: "Código para templates"
          },
          {
            nombre: "obligatoriobool",
            tipoitemformularioid: 6,
            etiqueta: "Campo obligatorio",
            respuesta: true
          },
          {
            nombre: "mostrarencertificadobool",
            tipoitemformularioid: 6,
            etiqueta: "Mostrar en certificado",
            respuesta: true
          },
          {
            nombre: "mostrarenplantilla",
            tipoitemformularioid: 6,
            etiqueta: "Mostrar en solicitud",
            respuesta: true
          },
          // Este flag decide qué campos viajan a URSEA (ver utils/URSEA.js),
          // así que solo se ofrece si el organismo tiene la integración.
          ...(conUrsea
            ? [
                {
                  nombre: "enviaraurseabool",
                  tipoitemformularioid: 6,
                  etiqueta: "Enviar a URSEA",
                  respuesta: true
                }
              ]
            : []),
          {
            nombre: "identificadorursea",
            tipoitemformularioid: 6,
            etiqueta: "Campo identificador",
            respuesta: false
          }
        ]
      }
    ],
    botones: [
      { etiqueta: "Agregar campo", callback: agregarCampo, validar: true }
    ]
  };

  const editarFormConfig = campoEditando
    ? {
        secciones: [
          {
            campos: [
              {
                nombre: "etiqueta",
                tipoitemformularioid: 1,
                etiqueta: "Etiqueta",
                requerido: true,
                respuesta: campoEditando.etiqueta
              },
              {
                nombre: "notas",
                tipoitemformularioid: 1,
                etiqueta: "Notas en formulario",
                respuesta: campoEditando.notas
              },
              {
                nombre: "unidades",
                tipoitemformularioid: 1,
                etiqueta: "Unidades",
                respuesta: campoEditando.unidades
              },
              {
                nombre: "tipo",
                tipoitemformularioid: 5,
                etiqueta: "Tipo de datos",
                requerido: true,
                sinOpcionInicial: true,
                opciones: [
                  { value: "string", etiqueta: "Texto" },
                  { value: "int", etiqueta: "Número entero" },
                  { value: "float", etiqueta: "Número decimal" }
                ],
                respuesta: campoEditando.tipo
              },
              {
                nombre: "opciones",
                tipoitemformularioid: 13,
                etiqueta: "Opciones",
                respuesta: campoEditando.opciones || []
              },
              {
                nombre: "nombre",
                tipoitemformularioid: 1,
                etiqueta: "Código para templates",
                respuesta: campoEditando.nombre
              },
              {
                nombre: "obligatoriobool",
                tipoitemformularioid: 6,
                etiqueta: "Campo obligatorio",
                respuesta: campoEditando.obligatoriobool
              },
              {
                nombre: "mostrarencertificadobool",
                tipoitemformularioid: 6,
                etiqueta: "Mostrar en certificado",
                respuesta: campoEditando.mostrarencertificadobool
              },
              {
                nombre: "mostrarenplantilla",
                tipoitemformularioid: 6,
                etiqueta: "Mostrar en solicitud",
                respuesta: campoEditando.mostrarenplantilla
              },
              ...(conUrsea
                ? [
                    {
                      nombre: "enviaraurseabool",
                      tipoitemformularioid: 6,
                      etiqueta: "Enviar a URSEA",
                      respuesta: campoEditando.enviaraurseabool
                    }
                  ]
                : []),
              {
                nombre: "identificadorursea",
                tipoitemformularioid: 6,
                etiqueta: "Campo identificador",
                respuesta: campoEditando.identificadorursea
              }
            ]
          }
        ],
        botones: [
          { etiqueta: "Guardar cambios", callback: editarCampo, validar: true }
        ]
      }
    : null;

  return (
    <>
      <Toast ref={toast} position="top-center" />
      <Menu model={menuItems} popup ref={menuRef} />
      <Card>
        <Titulo
          botones={
            <Boton icono negro callback={() => setSidebarNuevoVisible(true)}>
              <i className="pi pi-plus" />
            </Boton>
          }
        >
          Campos del producto
        </Titulo>
        <TablaFiltrable conFiltros={false}
          value={campos}
          nombreArchivo="campos_producto"
          reorderableRows
          onRowReorder={onRowReorder}
          emptyMessage="No hay campos definidos"
        >
          <Column rowReorder style={{ width: "3rem" }} />
          <Column field="etiqueta" header="Etiqueta" />
          <Column
            body={(rowData) => (
              <Boton
                icono
                negro
                chico
                callback={(e) => {
                  filaSeleccionada.current = rowData;
                  menuRef.current.toggle(e);
                }}
              >
                <i className="pi pi-ellipsis-v" />
              </Boton>
            )}
            style={{ width: "3rem", textAlign: "center" }}
          />
        </TablaFiltrable>
      </Card>

      <Sidebar
        visible={sidebarNuevoVisible}
        position="right"
        onHide={() => setSidebarNuevoVisible(false)}
        className="w-full lg:w-30rem md:w-30rem"
      >
        <Titulo>Nuevo campo del producto</Titulo>
        <Formulario
          config={agregarFormConfig}
          loader={loaderEnvio}
          key={campos.length}
        />
      </Sidebar>

      <Sidebar
        visible={sidebarEditarVisible}
        position="right"
        onHide={() => {
          setSidebarEditarVisible(false);
          setCampoEditando(null);
        }}
        className="w-full lg:w-30rem md:w-30rem"
      >
        {editarFormConfig && (
          <>
            <Titulo>Editar campo del producto</Titulo>
            <Formulario
              config={editarFormConfig}
              loader={loaderEnvio}
              key={campoEditando?.id}
            />
          </>
        )}
      </Sidebar>

      <Sidebar
        visible={sidebarAutocompletarVisible}
        position="right"
        onHide={() => {
          setSidebarAutocompletarVisible(false);
          setCampoEditando(null);
        }}
        className="w-full lg:w-30rem md:w-30rem"
      >
        {campoEditando && (
          <>
            <Titulo>Autocompletar {campoEditando.etiqueta}</Titulo>
            <CamposDisponibles />
            <Formulario
              key={`auto-${campoEditando.id}`}
              config={{
                secciones: [
                  {
                    campos: [
                      {
                        nombre: "autocompletarscript",
                        tipoitemformularioid: 11,
                        etiqueta: "Script",
                        respuesta: campoEditando.autocompletarScript ?? ""
                      }
                    ]
                  }
                ],
                botones: [
                  { etiqueta: "Guardar", callback: guardarAutocompletar }
                ]
              }}
              loader={loaderEnvio}
              onCambio={(c) => setBorradorScript(c.autocompletarscript ?? "")}
            />
            <SimuladorAutocompletar
              campoId={campoEditando.id}
              codigo={borradorScript}
            />
          </>
        )}
      </Sidebar>
    </>
  );
}
